import reduxStore from "..";
import { jflApi } from "../../common/authApi";
import { housesListAction } from "./housesList";
import { houseDataAction } from "./house";

// The property dropdowns read houseName and houseAccessCode off every entry in
// housesList (MainV2 sorts on houseName without a guard, so it has to be a
// string). houseAccessCode carries the store code rather than the id, because
// the assets endpoint is addressed by code and selection feeds straight into
// it. The store's own fields (_id, houseId, defaultStoreTimings) are kept
// alongside.
export const storeToHouseEntry = (item) => ({
  ...item,
  houseName: item.name ?? "",
  houseAccessCode: item.code,
});

// Goes through jflApi so a 401 refreshes the session and retries instead of
// dropping the list.
export const fetchStores = async () => {
  try {
    const response = await jflApi.get("/v1/stores");
    const stores = response.data?.data?.stores ?? [];
    const entries = stores.map(storeToHouseEntry);

    reduxStore.dispatch(housesListAction(entries));

    return { success: true, data: entries };
  } catch (error) {
    return {
      success: false,
      message:
        error.response?.data?.error?.errorMessage ||
        error.response?.data?.message ||
        error.message ||
        "Could not load stores",
    };
  }
};

// An asset is an appliance. It answers to the same energy APIs the old
// energiSync devices did (verified against /details), and the appliance
// dropdown labels off `name`, so assetName is mapped onto it.
export const assetToDevice = (asset) => ({
  ...asset,
  name: asset.assetName ?? "",
});

// An area is a room. The room dropdown filters on all five device buckets with
// .length and no guard, so every one has to be present even when empty.
export const areaToRoom = (area) => ({
  _id: area.areaId,
  roomName: area.areaName ?? "",
  energiSync: (area.assets ?? []).map(assetToDevice),
  thermoSmarts: [],
  weatherSensors: [],
  environSyncs: [],
  doorSensors: [],
});

// Loads one store's areas and assets, shaped as the houseData the room and
// appliance dropdowns already read. Addressed by store code, not id.
// The assets payload shaped as houseData, without dispatching anything. Split
// out so a page that only wants to read a property (Device Schedule) can reuse
// the mapping without also moving the dashboard's active property.
export const houseFromAssets = (payload, storeCode) => {
  const rooms = (payload?.areas ?? []).map(areaToRoom);

  // Assets that sit outside every area would otherwise be unreachable.
  const unassigned = payload?.unassigned ?? [];
  if (unassigned.length > 0) {
    rooms.push(
      areaToRoom({
        areaId: `${storeCode}-unassigned`,
        areaName: "Unassigned",
        assets: unassigned,
      })
    );
  }

  // _id is the store code, not payload.store.storeId: this endpoint is keyed
  // by code, and MainPage's effect feeds houseData._id straight into
  // selectedHouse, which the property dropdown then matches against
  // houseAccessCode. The mongo id stays available as storeId.
  return {
    ...payload?.store,
    _id: payload?.store?.storeCode ?? storeCode,
    rooms,
  };
};

export const storeAssetsPath = (storeCode) =>
  `/v1/stores/${encodeURIComponent(storeCode)}/assets`;

export const fetchStoreAssets = async (storeCode) => {
  try {
    const response = await jflApi.get(storeAssetsPath(storeCode));

    const houseData = houseFromAssets(response.data?.data ?? {}, storeCode);

    reduxStore.dispatch(houseDataAction(houseData));

    return { success: true, data: houseData };
  } catch (error) {
    return {
      success: false,
      message:
        error.response?.data?.error?.errorMessage ||
        error.response?.data?.message ||
        error.message ||
        "Could not load store assets",
    };
  }
};
